import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { isNotFound } from "@/api/client";
import { TenantMetadataPatch, blockersOf, getTenant, updateTenantMetadata } from "@/api/tenants";
import { Checkbox, Field, Fieldset, Input, Select } from "@/components/form";
import { MutationError, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import { TenantNotFound } from "@/components/TenantNotFound";
import { Button, FormActionBar, FormPlaceholder, Notice, Page, PageHeader } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { SSO_MODE_OPTIONS } from "@/lib/ssoMode";
import type { SsoMode } from "@/types/api";

/**
 * Edit an existing tenant: its display name, its admin contact and its options. A tenant is
 * CREATED by the onboarding wizard (`/onboarding`), which asks for the identifier, the two factors
 * and the cost display up front — the identifier fixes the tenant's address, so it is chosen there,
 * before anything exists, and shown here read-only.
 */
interface FormState {
  display_name: string;
  admin_email: string;
  admin_given_name: string;
  admin_family_name: string;
  cost_estimation_ui_enabled: boolean;
  mfa_required: boolean;
  sso_mode: SsoMode;
}

export function TenantEditPage() {
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = auth.user?.id_token ?? "";

  const existing = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => getTenant(token, tenantId),
    enabled: !!token && !!tenantId,
  });

  const [state, setState] = useState<FormState>({
    display_name: "",
    admin_email: "",
    admin_given_name: "",
    admin_family_name: "",
    cost_estimation_ui_enabled: false,
    mfa_required: false,
    sso_mode: "mixed",
  });
  const [primed, setPrimed] = useState(false);
  // Dirty = what the form shows differs from what it was opened with (the values of the render where
  // it was primed), and the way out of the page is held while it is.
  const { dirty, reset: markSaved } = useDirtyValues(state, primed);
  const guard = useUnsavedChanges(dirty);
  useEffect(() => {
    if (!existing.data || primed) return;
    const m = existing.data.metadata;
    setState({
      display_name: m.displayName,
      admin_email: m.adminEmail,
      admin_given_name: m.adminGivenName,
      admin_family_name: m.adminFamilyName,
      cost_estimation_ui_enabled: m.costEstimationUiEnabled,
      mfa_required: m.mfaRequired,
      sso_mode: m.ssoMode,
    });
    setPrimed(true);
  }, [existing.data, primed]);

  const updateMut = useMutation({
    mutationFn: (patch: TenantMetadataPatch) => updateTenantMetadata(token, tenantId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      markSaved();
      guard.leave(`/tenants/${tenantId}`);
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    updateMut.mutate({
      display_name: state.display_name,
      admin_email: state.admin_email,
      admin_given_name: state.admin_given_name,
      admin_family_name: state.admin_family_name,
      cost_estimation_ui_enabled: state.cost_estimation_ui_enabled,
      mfa_required: state.mfa_required,
      sso_mode: state.sso_mode,
    });
  }

  // The title comes from the address, so loading and failing both keep it. A
  // failed load must not open the form: saved blank, it would overwrite the tenant.
  // "Loading" includes PAUSED (the browser is offline), see ``StageForm``.
  const header = (
    <PageHeader
      back={{ to: `/tenants/${tenantId}`, label: existing.data?.metadata.displayName ?? tenantId }}
      title={`Edit ${tenantId}`}
    />
  );
  if (isLoadingOrPaused(existing)) return <FormPlaceholder header={header} />;
  if (existing.isLoadingError && isNotFound(existing.error)) return <TenantNotFound tenantId={tenantId} width="form" />;
  if (existing.isLoadingError) return <FormPlaceholder header={header} failure={loadFailure(existing)} />;

  return (
    <Page width="form">
      {header}

      <form onSubmit={onSubmit}>
        <div className="card space-y-5">
          <Field label="Display name">
            <Input
              value={state.display_name}
              onChange={(e) => setState((s) => ({ ...s, display_name: e.target.value }))}
              required
            />
          </Field>

          <Field label="Identifier" help="Cannot be changed after creation.">
            <Input mono suffix=".pulsestation.io" value={tenantId} disabled />
          </Field>

          <Fieldset
            legend="Admin contact"
            help="This person receives an email invitation (with a temporary password) after each successful deployment. They become the first administrator of the tenant's workspace."
          >
            <div className="mt-3 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="First name">
                  <Input
                    value={state.admin_given_name}
                    onChange={(e) => setState((s) => ({ ...s, admin_given_name: e.target.value }))}
                    required
                    maxLength={64}
                  />
                </Field>
                <Field label="Last name">
                  <Input
                    value={state.admin_family_name}
                    onChange={(e) => setState((s) => ({ ...s, admin_family_name: e.target.value }))}
                    required
                    maxLength={64}
                  />
                </Field>
              </div>
              <Field label="Email">
                <Input
                  type="email"
                  value={state.admin_email}
                  onChange={(e) => setState((s) => ({ ...s, admin_email: e.target.value }))}
                  required
                  maxLength={254}
                />
              </Field>
            </div>
          </Fieldset>

          <Fieldset legend="Options" className="border-t border-line pt-4">
            <div className="mt-2 space-y-2">
              <Checkbox
                label={<span className="font-medium">Cost estimation display</span>}
                description={
                  <>
                    Shows the "Estimated Cost" card in the tenant's GUI. The
                    calculation and the API always stay active; this setting
                    only controls the display. Takes effect on the tenant's next
                    deployment.
                  </>
                }
                checked={state.cost_estimation_ui_enabled}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    cost_estimation_ui_enabled: e.target.checked,
                  }))
                }
              />
              <Checkbox
                label={<span className="font-medium">Require two-factor authentication</span>}
                description={
                  <>
                    Applies to every stage of this tenant. Users without a second
                    factor will be asked to set one up at their next sign-in.
                  </>
                }
                checked={state.mfa_required}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    mfa_required: e.target.checked,
                  }))
                }
              />
              <Field label="Sign-in">
                <Select
                  value={state.sso_mode}
                  options={SSO_MODE_OPTIONS}
                  onChange={(e) => setState((s) => ({ ...s, sso_mode: e.target.value as SsoMode }))}
                />
              </Field>
              {state.sso_mode === "exclusive" && (
                <Notice tone="warning">
                  Directory-only sign-in needs a company directory on every environment, at least one
                  emergency account and two-factor authentication. Emergency accounts are the only ones
                  left with a password.
                </Notice>
              )}
              {/* A sibling of the checkbox, not inside its description (a <span>): a message
                  box is a block, and a bare tinted span carried its meaning by colour alone. */}
              {state.mfa_required && (
                <Notice tone="warning">
                  The appliance local GUI cannot yet answer a second-factor
                  challenge: an operator with one armed is locked out of it.
                  Do not turn this on for a tenant with appliances in service
                  until the gateway image ships that support.
                </Notice>
              )}
            </div>
          </Fieldset>

          <p className="text-xs text-ink-muted border-t border-line pt-3">
            Allowed cloud / customer instances are configured per stage —
            set them when creating or editing a stage.
          </p>
        </div>

        <FormActionBar
          dirty={dirty}
          submitLabel="Save"
          pending={updateMut.isPending}
          onCancel={() => navigate(-1)}
          error={
            <MutationError
              mutation={updateMut}
              title="Could not save the tenant"
              view={(error) => {
                // A refused sign-in mode: the server says what is missing, one reason per line.
                const blockers = blockersOf(error);
                if (!blockers) return undefined;
                return (
                  <Notice
                    tone="danger"
                    role="alert"
                    title="Could not save the tenant"
                    actions={
                      <Button variant="ghost" size="sm" onClick={() => updateMut.reset()}>
                        Dismiss
                      </Button>
                    }
                  >
                    <p>Directory-only sign-in is not possible yet:</p>
                    <ul className="mt-1 list-disc pl-5">
                      {blockers.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </Notice>
                );
              }}
            />
          }
        />
      </form>
    </Page>
  );
}
